// Libararies
import _ from 'lodash';
import React from 'react';

// Supermove
import {DataLoader} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useQuery,
  useScrollView,
  ResponsiveType,
  useDebouncedCallback,
  useEffect,
} from '@supermove/hooks';
import {
  CommunicationModel,
  ProjectModel,
  TextMessageTemplateModel,
  UserModel,
} from '@supermove/models';

// App
import ErrorState from '@shared/design/components/EmptyState/ErrorState';
import ErrorCatcher from '@shared/modules/Error/components/ErrorCatcher';
import ProjectWidgetKind from '@shared/modules/Project/enums/ProjectWidgetKind';
import TextMessageTemplateCategory from '@shared/modules/Sms/enums/TextMessageTemplateCategory';
import CommunicationsInboxProjectConversationNoResults from 'modules/Communication/CommunicationInbox/ProjectConversation/components/CommunicationInboxConversationNoResults';
import CommunicationInboxProjectConversationFooter from 'modules/Communication/CommunicationInbox/ProjectConversation/components/footer/CommunicationInboxProjectConversationFooter';
import {CommunicationInboxConversationUrlFilterType} from 'modules/Communication/CommunicationInbox/types/CommunicationInboxUrlFilterType';
import {useCommunicationNewSseSubscription} from 'modules/Communication/hooks/useCommunicationNewSseSubscription';
import ProjectWidgetsToolbar from 'modules/Project/V2/Show/components/ProjectWidgetsToolbar';
import CommunicationsWidgetBody from 'modules/Project/V2/Show/components/widgets/CommunicationsWidget/CommunicationsWidgetBody';
import CommunicationsWidgetFooter from 'modules/Project/V2/Show/components/widgets/CommunicationsWidget/CommunicationsWidgetFooter';

const Footer = ({
  user,
  viewer,
  project,
  refetch,
  urlFilters,
  responsive,
  textMessageTemplatesByCategory,
}: {
  user: UserModel;
  viewer: UserModel;
  project: ProjectModel;
  refetch: () => void;
  urlFilters: CommunicationInboxConversationUrlFilterType;
  responsive: ResponsiveType;
  textMessageTemplatesByCategory: TextMessageTemplateModel[];
}) => {
  const activeActionType = urlFilters.get('actionType') || 'SMS';
  const replyThreadUuid = urlFilters.get('replyThreadUuid');
  const footerExpanded = urlFilters.get('footerExpanded') ?? undefined;

  return (
    <React.Fragment>
      {project.organization.features.isEnabledInboxActions ? (
        <ErrorCatcher ErrorComponent={() => <ErrorState title={'Something went wrong.'} />}>
          <CommunicationInboxProjectConversationFooter
            projectUuid={project.uuid}
            customerUuid={user.uuid}
            refetch={refetch}
            selectedTextMessageTemplateUuid={urlFilters.get('textMessageTemplateUuid')}
            replyThreadUuid={replyThreadUuid}
            setReplyThreadUuid={(newReplyThreadUuid) => {
              urlFilters.handleUpdate({
                replyThreadUuid: newReplyThreadUuid,
                actionType: newReplyThreadUuid ? 'EMAIL' : activeActionType,
              });
            }}
            activeActionType={activeActionType}
            showSingleActionType={urlFilters.get('communicationTag')}
            updateActiveActionType={(newActiveActionType) => {
              urlFilters.handleUpdate({
                actionType: newActiveActionType,
                replyThreadUuid: newActiveActionType === 'EMAIL' ? replyThreadUuid : undefined,
                footerExpanded: newActiveActionType === 'EMAIL' ? footerExpanded : undefined,
              });
            }}
            footerExpanded={!!footerExpanded}
            setFooterExpanded={(newFooterExpanded) => {
              urlFilters.handleUpdate({footerExpanded: newFooterExpanded ? true : undefined});
            }}
          />
        </ErrorCatcher>
      ) : (
        <CommunicationsWidgetFooter
          customer={user}
          viewer={viewer}
          refetch={refetch}
          projectId={project.id}
          textMessageTemplates={textMessageTemplatesByCategory}
          selectedTextMessageTemplate={_.find(
            textMessageTemplatesByCategory,
            (template) => template.uuid === urlFilters.get('textMessageTemplateUuid'),
          )}
        />
      )}
      {project.organization.features.isEnabledConversationsPageProjectInformation &&
      !responsive.desktop &&
      !footerExpanded ? (
        <ProjectWidgetsToolbar
          urlFilters={urlFilters}
          project={project}
          widgets={ProjectWidgetKind.getCommunicationInboxWidgets(project)}
        />
      ) : null}
    </React.Fragment>
  );
};

const CommunicationsInboxProjectConversationContent = ({
  project,
  urlFilters,
  responsive,
  setCommunicationCounts,
}: {
  project: ProjectModel;
  urlFilters: CommunicationInboxConversationUrlFilterType;
  responsive: ResponsiveType;
  setCommunicationCounts: (counts: {
    emailCount: number;
    textMessageCount: number;
    callCount: number;
  }) => void;
}) => {
  const conversationsScrollView = useScrollView();

  const {loading, data, refetch} = useQuery<{
    viewer: UserModel;
    user: UserModel;
    project: ProjectModel;
    projectCommunications: CommunicationModel[];
  }>(CommunicationsInboxProjectConversationContent.query, {
    fetchPolicy: 'network-only',
    variables: {
      userUuid: project.client.primaryContact.uuid,
      projectId: project.id,
      projectUuid: project.uuid,
      categories: [TextMessageTemplateCategory.CUSTOMER],
      searchQuery: urlFilters.get('communicationSearchQuery') ?? '',
    },
  });

  const debouncedRefetch = useDebouncedCallback(refetch, 1000);
  const {latestEvent, subscribeToProject, unsubscribe} = useCommunicationNewSseSubscription();
  const footerExpanded = urlFilters.get('footerExpanded');

  useEffect(() => {
    subscribeToProject(project.id);

    return () => {
      unsubscribe();
    };
  }, [project]);

  useEffect(() => {
    if (latestEvent && project.organization.features.isEnabledRealTimeMessages) {
      debouncedRefetch();
    }
  }, [latestEvent]);

  useEffect(() => {
    if (data?.projectCommunications?.length) {
      const counts = data.projectCommunications.reduce(
        (acc, communication) => {
          if (communication.email) {
            acc.emailCount += 1;
          }
          if (communication.textMessage) {
            acc.textMessageCount += 1;
          }
          if (communication.call) {
            acc.callCount += 1;
          }
          return acc;
        },
        {emailCount: 0, textMessageCount: 0, callCount: 0},
      );
      setCommunicationCounts(counts);
    }
  }, [data?.projectCommunications]);

  return (
    <React.Fragment>
      <DataLoader loading={loading} data={data}>
        {({loadedData}) => {
          const activeTag = urlFilters.get('communicationTag');
          const filteredCommunications = (() => {
            if (!activeTag) {
              return loadedData.projectCommunications;
            }
            return loadedData.projectCommunications.filter((communication) => {
              if (activeTag === 'EMAIL') {
                return !!communication.email;
              }
              if (activeTag === 'SMS') {
                return !!communication.textMessage;
              }
              if (activeTag === 'CALL') {
                return !!communication.call;
              }
              return false;
            });
          })();

          const {textMessageTemplatesByCategory} = loadedData.project.organization;
          const FooterComponent = (
            <Footer
              user={loadedData.user}
              viewer={loadedData.viewer}
              project={loadedData.project}
              refetch={refetch}
              urlFilters={urlFilters}
              responsive={responsive}
              textMessageTemplatesByCategory={textMessageTemplatesByCategory}
            />
          );

          if (!filteredCommunications?.length && !loading) {
            return (
              <CommunicationsInboxProjectConversationNoResults
                responsive={responsive}
                footer={FooterComponent}
              />
            );
          }
          return (
            <React.Fragment>
              {footerExpanded ? null : (
                <CommunicationsWidgetBody
                  viewer={loadedData.viewer}
                  customer={loadedData.user}
                  conversations={filteredCommunications}
                  conversationsScrollView={conversationsScrollView}
                  handleReplyThread={(threadUuid) =>
                    urlFilters.handleUpdate({replyThreadUuid: threadUuid, actionType: 'EMAIL'})
                  }
                />
              )}
              {FooterComponent}
            </React.Fragment>
          );
        }}
      </DataLoader>
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
CommunicationsInboxProjectConversationContent.query = gql`
  ${CommunicationsWidgetBody.fragment}
  ${CommunicationsWidgetFooter.fragment}
  ${ProjectWidgetsToolbar.fragment}
  ${ProjectWidgetKind.getCommunicationInboxWidgets.fragment}

  query CommunicationsInboxProjectConversationContent(
    $userUuid: String!,
    $projectId: Int!,
    $projectUuid: String!,
    $categories: [String],
    $searchQuery: String,
    $conversationKinds: [String],
  ) {
    ${gql.query}
    viewer {
        id
        ...CommunicationsWidgetFooter_Viewer
        ...CommunicationsWidgetBody_Viewer
      }
      user(uuid: $userUuid) {
        id
        uuid
        ...CommunicationsWidgetFooter_User
        ...CommunicationsWidgetBody_User
      }
      project(uuid: $projectUuid) {
        id
        organization {
          id
          textMessageTemplatesByCategory(categories: $categories) {
            id
            uuid
            ...CommunicationsWidgetFooter_TextMessageTemplate
          }
          features {
           isEnabledConversationsPageProjectInformation: isEnabled(
              feature: "CONVERSATIONS_PAGE_PROJECT_INFORMATION"
            )
            isEnabledInboxActions: isEnabled(feature: "INBOX_ACTIONS")
      }
        }
        ...ProjectWidgetsToolbar
        ...ProjectWidgetKind_getCommunicationInboxWidgets
      }
      projectCommunications(projectId: $projectId, searchQuery: $searchQuery, conversationKinds: $conversationKinds) {
        id
        email {
          id
        }
        textMessage {
          id
        }
        call {
          id
        }
        ...CommunicationsWidgetBody_Communication
      }
  }
`;

CommunicationsInboxProjectConversationContent.fragment = gql`
  fragment CommunicationsInboxProjectConversationContent on Project {
    id
    uuid
    client {
      id
      uuid
      primaryContact {
        id
        uuid
      }
    }
    organization {
      id
      slug
      features {
        isEnabledRealTimeMessages: isEnabled(feature: "REAL_TIME_MESSAGES")
      }
    }
  }
`;

export default CommunicationsInboxProjectConversationContent;
